import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { submitLead, track } from "@/lib/api";
import { formOptions } from "@/data/content";

const inputCls =
  "w-full bg-white border border-ink/20 rounded-none px-4 py-3.5 text-base text-ink placeholder:text-ink/30 focus:outline-none focus:ring-1 focus:ring-ink focus:border-ink transition-colors";
const labelCls = "block text-xs uppercase tracking-[0.2em] font-semibold text-smoke mb-2";

function Field({ label, required, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className={labelCls}>
        {label} {required && <span className="text-accent">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function LeadForm({ mode, presetProjectType }) {
  const isVisit = mode === "site_visit";
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    location: "",
    project_type: presetProjectType || "commercial-upfit",
    property_type: "office",
    timeline: "1-3-months",
    budget: "not-sure",
    visit_timing: "flexible",
    message: "",
    website: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = { ...form, lead_type: mode };
      if (!isVisit) {
        payload.timeline = "";
        payload.budget = "";
        payload.visit_timing = "";
      }
      await submitLead(payload);
      track("lead_submit", mode);
      navigate("/thank-you", { state: { mode } });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or call us directly.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7" data-testid="lead-form">
      <Field label="Full Name" required testid="field-name">
        <input className={inputCls} value={form.name} onChange={set("name")} required minLength={2} placeholder="Jane Doe" data-testid="lead-name-input" />
      </Field>
      <Field label="Company" testid="field-company">
        <input className={inputCls} value={form.company} onChange={set("company")} placeholder="Company or organization" data-testid="lead-company-input" />
      </Field>
      <Field label="Email" required testid="field-email">
        <input type="email" className={inputCls} value={form.email} onChange={set("email")} required placeholder="you@company.com" data-testid="lead-email-input" />
      </Field>
      <Field label="Phone" required testid="field-phone">
        <input type="tel" className={inputCls} value={form.phone} onChange={set("phone")} required minLength={7} placeholder="(843) 555-0000" data-testid="lead-phone-input" />
      </Field>
      <Field label="Project Location" required testid="field-location">
        <input className={inputCls} value={form.location} onChange={set("location")} required placeholder="City or address in South Carolina" data-testid="lead-location-input" />
      </Field>
      <Field label="Project Type" required testid="field-project-type">
        <select className={inputCls} value={form.project_type} onChange={set("project_type")} data-testid="lead-project-type-select">
          {formOptions.projectTypes.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Property Type" required testid="field-property-type">
        <select className={inputCls} value={form.property_type} onChange={set("property_type")} data-testid="lead-property-type-select">
          {formOptions.propertyTypes.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      {isVisit && (
        <>
          <Field label="Rough Timeline" required testid="field-timeline">
            <select className={inputCls} value={form.timeline} onChange={set("timeline")} data-testid="lead-timeline-select">
              {formOptions.timelines.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Rough Budget" required testid="field-budget">
            <select className={inputCls} value={form.budget} onChange={set("budget")} data-testid="lead-budget-select">
              {formOptions.budgets.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Preferred Visit Timing" required testid="field-visit-timing">
            <select className={inputCls} value={form.visit_timing} onChange={set("visit_timing")} data-testid="lead-visit-timing-select">
              {formOptions.visitTimings.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </>
      )}
      <div className="md:col-span-2" data-testid="field-message">
        <label className={labelCls}>Tell us about the project</label>
        <textarea
          className={`${inputCls} min-h-[130px] resize-y`}
          value={form.message}
          onChange={set("message")}
          placeholder="Square footage, current condition, what you need done, and anything about timing we should know."
          data-testid="lead-message-textarea"
        />
      </div>
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={set("website")}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      <div className="md:col-span-2 pt-2">
        <button
          type="submit"
          disabled={submitting}
          className="group inline-flex items-center gap-3 bg-ink text-paper font-semibold uppercase tracking-widest text-sm px-10 py-5 hover:bg-accent transition-colors disabled:opacity-60"
          data-testid="lead-form-submit-button"
        >
          {submitting ? "Sending..." : isVisit ? "Request the Site Visit" : "Request the Quote"}
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </button>
        <p className="mt-4 text-xs text-smoke leading-relaxed max-w-md">
          Every request is reviewed by a person within one business day. No spam, no robo-calls — a call or email from our team to lock in next steps.
        </p>
      </div>
    </form>
  );
}
